import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('SkillsAPI', 'api');

const skillsClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 30000,
});

/** Typed error thrown by all skills API functions. */
export class SkillsApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SkillsApiError';
  }
}

/**
 * Normalises any caught value into a SkillsApiError, extracting the backend `detail` message
 * where available.
 */
const parseError = (error: unknown): SkillsApiError => {
  handleSessionExpired(error);
  if (!axios.isAxiosError(error)) {
    return new SkillsApiError(error instanceof Error ? error.message : 'Request failed');
  }
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return new SkillsApiError(detail);
  }
  return new SkillsApiError(error.message);
};

/** Summary of a global skill template as returned by the templates list endpoint. */
export interface SkillTemplateListItemDTO {
  template_key: string;
  title: string;
  description: string;
  author: string | null;
  tags: string[];
  meta_version: string | null;
}

/** Full detail of a global skill template including the full markdown body. */
export interface SkillTemplateDetailDTO {
  template_key: string;
  title: string;
  body_markdown: string;
  description: string;
  author: string | null;
  tags: string[];
  meta_version: string | null;
}

/** A versioned skill document belonging to a project; each save creates a new version in the chain. */
export interface ProjectSkillVersionDTO {
  id: string;
  client_id: string;
  project_id: string;
  skill_chain_root_id: string;
  previous_version_id: string | null;
  title: string;
  description: string;
  body_markdown: string;
  tags: string[];
  forked_from_template_key: string | null;
  version_author_sub: string;
  version_author_name: string;
  created_at: string;
}

/** Response from the skills list endpoint. */
export interface ProjectSkillListResponseDTO {
  skills: ProjectSkillVersionDTO[];
}

/** Request body for creating a new skill version. */
export interface CreateProjectSkillRequestDTO {
  title: string;
  description?: string;
  body_markdown: string;
  tags?: string[];
  forked_from_template_key?: string | null;
  previous_version_id?: string | null;
}

/**
 * Lists all available global skill templates.
 * GET /skills/templates
 *
 * @returns Array of skill template summaries.
 */
export async function fetchSkillTemplates(
  accessToken: string,
): Promise<SkillTemplateListItemDTO[]> {
  try {
    const response = await skillsClient.get<SkillTemplateListItemDTO[]>(`/skills/templates`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    const parsed = parseError(error);
    logger.error('Failed to list skill templates', parsed);
    throw parsed;
  }
}

/**
 * Loads the full body of a single global skill template.
 * GET /skills/templates/{templateKey}
 *
 * @returns The template detail including markdown body.
 */
export async function fetchSkillTemplateDetail(
  accessToken: string,
  templateKey: string,
): Promise<SkillTemplateDetailDTO> {
  try {
    const response = await skillsClient.get<SkillTemplateDetailDTO>(
      `/skills/templates/${encodeURIComponent(templateKey)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseError(error);
    logger.error('Failed to load skill template', parsed);
    throw parsed;
  }
}

/**
 * Lists all skill versions saved by the current user.
 * GET /skills
 *
 * @returns List of the user's saved skill versions.
 */
export async function fetchSkills(accessToken: string): Promise<ProjectSkillListResponseDTO> {
  try {
    const response = await skillsClient.get<ProjectSkillListResponseDTO>(`/skills`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    const parsed = parseError(error);
    logger.error('Failed to list skills', parsed);
    throw parsed;
  }
}

/**
 * Loads a single skill version by its unique version ID.
 * GET /skills/{versionId}
 *
 * @returns The full skill version record including markdown body.
 */
export async function fetchSkillVersion(
  accessToken: string,
  versionId: string,
): Promise<ProjectSkillVersionDTO> {
  try {
    const response = await skillsClient.get<ProjectSkillVersionDTO>(
      `/skills/${encodeURIComponent(versionId)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseError(error);
    logger.error('Failed to load skill version', parsed);
    throw parsed;
  }
}

/**
 * Creates a new skill version, optionally linking it to a previous version or a forked template.
 * POST /skills
 *
 * @returns The newly created skill version record.
 */
export async function createSkillVersion(
  accessToken: string,
  body: CreateProjectSkillRequestDTO,
): Promise<ProjectSkillVersionDTO> {
  try {
    const response = await skillsClient.post<ProjectSkillVersionDTO>(`/skills`, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    const parsed = parseError(error);
    logger.error('Failed to create skill version', parsed);
    throw parsed;
  }
}
